import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Prompt, useHistory } from 'react-router-dom'

import {
  AiInventoryDaiChat,
  AiInventoryDaiReview,
  Container,
  Page,
} from 'components'

import useAuth from 'hooks/useAuth'
import useBeforeunload from 'hooks/useBeforeunload'
import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import useAiInventoryDaiChat, {
  mapAiInventoryToPanelItem,
} from 'hooks/useAiInventoryDaiChat'
import * as service from 'service'

import { routes } from 'Routes'

// Achata o catálogo de risco (opções + filhas) num mapa { id: label } para
// resolver os rótulos das categorias selecionadas no card do painel.
const buildRiskOptionsById = (options) => {
  const map = {}
  const add = (option) => {
    map[option.id] = option.label
    ;(option.children || []).forEach(add)
  }
  options.forEach(add)
  return map
}

const APP_NAME = 'ai_inventory_agent'
const CHAT_TITLE = 'Mapeamento de IA'
// Mensagem do diálogo nativo do navegador (voltar/navegar para fora da tela).
const EXIT_MESSAGE =
  'Ao sair, você perderá o histórico da sua conversa com a DAI. Deseja sair?'

const AiInventoryDaiRegistration = () => {
  const history = useHistory()
  const { user } = useAuth()
  const snackbar = useSnackbar()

  // Revisão é uma view do MESMO componente (não navega) — assim a sessão de
  // chat (mensagens, item ativo, contexto do agente) não é perdida ao voltar.
  const [reviewing, setReviewing] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [concluding, setConcluding] = useState(false)

  // Catálogo de risco para resolver os rótulos das categorias no card. Mantido
  // num ref para que os loaders (useCallback estáveis) sempre leiam o atual.
  const riskOptionsByIdRef = useRef({})
  const { response: riskOptionsResponse } = useFetch(
    service.dponet.aiInventoryRiskOptions.get,
    undefined,
    [],
  )
  useEffect(() => {
    const options = riskOptionsResponse?.data?.aiInventoryRiskOptions || []
    riskOptionsByIdRef.current = buildRiskOptionsById(options)
  }, [riskOptionsResponse])

  const loadItems = useCallback(async () => {
    // detailed=true → serializer completo (finalidades, modo, intervenção, risco).
    const response = await service.dponet.aiInventory.get({
      detailed: true,
      perPage: 1000,
    })
    const inventories = response?.data?.aiInventoryItems || []
    return inventories.map((inventory) =>
      mapAiInventoryToPanelItem(inventory, riskOptionsByIdRef.current),
    )
  }, [])

  // Busca incremental de um único item (após cada turno do chat) — evita
  // recarregar a listagem detalhada inteira a cada mensagem.
  const loadItem = useCallback(async (aiInventoryId) => {
    const response = await service.dponet.aiInventory.show(aiInventoryId)
    const inventory = response?.data?.aiInventoryItem
    return inventory
      ? mapAiInventoryToPanelItem(inventory, riskOptionsByIdRef.current)
      : null
  }, [])

  const {
    messages,
    items,
    activeItemId,
    sendMessage,
    isSending,
    abortMessage,
  } = useAiInventoryDaiChat({
    chatClient: service.dai.chat,
    appName: APP_NAME,
    userId: String(user?.id),
    loadItems,
    loadItem,
  })

  // Saídas intencionais (botão Voltar confirmado na modal, concluir cadastro)
  // liberam o <Prompt> para não disparar o confirm nativo em duplicidade.
  const allowExitRef = useRef(false)

  const leaveTo = (path) => {
    allowExitRef.current = true
    history.push(path)
  }

  const goToList = () => leaveTo(routes.aiInventory.all)

  const handleConclude = async () => {
    setConcluding(true)
    try {
      await service.dponet.aiInventory.confirmVeracity(
        items.map((item) => item.id),
      )
      leaveTo(routes.aiInventory.all)
    } catch {
      snackbar.open({
        message: 'Não foi possível concluir o cadastro. Tente novamente.',
        variant: 'error',
      })
      setConcluding(false)
    }
  }

  // Só oferece "revisar e concluir" quando já há ao menos um item cadastrado.
  const showConclusion = items.length > 0

  // Avisa ao fechar/recarregar a aba (diálogo nativo do navegador).
  useBeforeunload((event) => event.preventDefault())

  return (
    <Page title="Cadastro guiado pela DAI">
      {/* Voltar do navegador / navegar para fora: confirm nativo, exceto quando
          a saída já foi confirmada (botão Voltar via modal ou concluir). */}
      <Prompt message={() => allowExitRef.current || EXIT_MESSAGE} />
      <Container
        maxWidth={false}
        spacingXl={3}
        spacingLg={3}
        spacingMd={3}
        spacingSm={2}
      >
        {reviewing ? (
          <AiInventoryDaiReview
            items={items}
            confirmed={confirmed}
            onConfirmChange={setConfirmed}
            onBack={() => setReviewing(false)}
            onConclude={handleConclude}
            loading={concluding}
          />
        ) : (
          <AiInventoryDaiChat
            title={CHAT_TITLE}
            messages={messages}
            items={items}
            activeItemId={activeItemId}
            showConclusion={showConclusion}
            onConclude={() => setReviewing(true)}
            onSend={sendMessage}
            onBack={goToList}
            isSending={isSending}
            abortMessage={abortMessage}
            userName={user?.name}
          />
        )}
      </Container>
    </Page>
  )
}

export default AiInventoryDaiRegistration
